/**
 * WebLLM - Browser-based LLM (WebGPU)
 * VRAM bazli model onerileri + tarayici destegi
 */
import axios from 'axios'
import { AlertCircle, Check, ChevronDown, ChevronRight, Download, Globe, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface WebLLMModel {
  id: string
  name: string
  size_gb: number
  is_downloaded: boolean
  is_active: boolean
  vram_required_gb: number
}

interface WebLLMStatus {
  is_enabled: boolean
  active_model: string | null
  models: WebLLMModel[]
}

const WEBGPU_VRAM_TIERS = [
  {
    vram: '4 GB', color: '#f59e0b', badge: 'Entry',
    note: 'Integrated GPU (Intel Iris, AMD Vega) or entry NVIDIA/AMD',
    models: [
      { id: 'Llama-3.2-1B-Instruct-q4f32_1-MLC', name: 'Llama 3.2 1B (Q4)', size_gb: 0.8, vram_required_gb: 1.5, quality: 'Basic' },
      { id: 'Phi-3.5-mini-instruct-q4f16_1-MLC', name: 'Phi-3.5 Mini (Q4)', size_gb: 2.2, vram_required_gb: 3.5, quality: 'Good' },
      { id: 'gemma-2-2b-it-q4f16_1-MLC', name: 'Gemma 2 2B (Q4)', size_gb: 1.6, vram_required_gb: 3.0, quality: 'Good' },
    ]
  },
  {
    vram: '8 GB', color: '#3b82f6', badge: 'Standard',
    note: 'NVIDIA RTX 3060 / AMD RX 6600 / Apple M1 Pro',
    models: [
      { id: 'Llama-3.1-8B-Instruct-q4f32_1-MLC', name: 'Llama 3.1 8B (Q4)', size_gb: 4.7, vram_required_gb: 6.0, quality: 'Very Good' },
      { id: 'Mistral-7B-Instruct-v0.3-q4f16_1-MLC', name: 'Mistral 7B (Q4)', size_gb: 4.1, vram_required_gb: 5.5, quality: 'Very Good' },
      { id: 'Qwen2.5-7B-Instruct-q4f16_1-MLC', name: 'Qwen 2.5 7B (Q4)', size_gb: 4.4, vram_required_gb: 6.0, quality: 'Very Good' },
    ]
  },
  {
    vram: '16 GB', color: '#8b5cf6', badge: 'Pro',
    note: 'NVIDIA RTX 3080/4070 / AMD RX 7800 XT / Apple M2 Pro',
    models: [
      { id: 'Llama-3.1-13B-Instruct-q4f16_1-MLC', name: 'Llama 3.1 13B (Q4)', size_gb: 7.9, vram_required_gb: 10.0, quality: 'Excellent' },
      { id: 'Qwen2.5-14B-Instruct-q4f16_1-MLC', name: 'Qwen 2.5 14B (Q4)', size_gb: 8.9, vram_required_gb: 12.0, quality: 'Excellent' },
      { id: 'Mistral-Nemo-12B-q4f16_1-MLC', name: 'Mistral Nemo 12B (Q4)', size_gb: 7.1, vram_required_gb: 9.0, quality: 'Excellent' },
    ]
  },
  {
    vram: '32 GB', color: '#10b981', badge: 'Power',
    note: 'NVIDIA RTX 3090/4090 / AMD RX 7900 XTX / Apple M2 Ultra',
    models: [
      { id: 'Llama-3.1-70B-Instruct-q3f16_1-MLC', name: 'Llama 3.1 70B (Q3)', size_gb: 26.0, vram_required_gb: 28.0, quality: 'Near GPT-4' },
      { id: 'Qwen2.5-32B-Instruct-q4f16_1-MLC', name: 'Qwen 2.5 32B (Q4)', size_gb: 19.0, vram_required_gb: 22.0, quality: 'Near GPT-4' },
    ]
  },
]

const BROWSER_SUPPORT = [
  { name: 'Chrome 113+', supported: true, os: 'Windows / macOS / Linux' },
  { name: 'Edge 113+', supported: true, os: 'Windows / macOS' },
  { name: 'Safari 17+', supported: true, os: 'macOS / iOS (Metal)' },
  { name: 'Firefox', supported: false, os: 'All (unstable)' },
]

export default function WebLLMPage() {
  const [status, setStatus] = useState<WebLLMStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<Record<string, boolean>>({})
  const [openVram, setOpenVram] = useState<string | null>('8 GB')

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/aicloud/webllm/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStatus(resp.data as WebLLMStatus)
    } catch {
      setStatus({ is_enabled: false, active_model: null, models: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchStatus() }, [])

  const handleToggleEnable = async () => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/webllm/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Failed')
    }
  }

  const handleDownload = async (modelId: string) => {
    setDownloading(prev => ({ ...prev, [modelId]: true }))
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/webllm/download`, { model_id: modelId }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess(`${modelId} download started`)
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Download failed')
    } finally {
      setDownloading(prev => ({ ...prev, [modelId]: false }))
    }
  }

  const handleActivate = async (modelId: string) => {
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/aicloud/webllm/activate`, { model_id: modelId }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchStatus()
    } catch (e) {
      setError((e as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Failed')
    }
  }

  const handleDelete = async (modelId: string) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/webllm/models/${modelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      void fetchStatus()
    } catch { setError('Failed to delete') }
  }

  if (loading) {return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>}

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>WebLLM</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Run LLMs directly in your browser via WebGPU. Offline, private, zero cost.
          </p>
        </div>
        <button
          onClick={() => { void handleToggleEnable() }}
          style={{ padding: '8px 14px', background: status?.is_enabled ? '#ef4444' : '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
        >
          {status?.is_enabled ? 'Disable WebLLM' : 'Enable WebLLM'}
        </button>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {/* Tarayici destegi */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {BROWSER_SUPPORT.map(b => (
          <div key={b.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', background: b.supported ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${b.supported ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`, fontSize: '12px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: b.supported ? '#10b981' : '#ef4444' }} />
            <span style={{ fontWeight: 600 }}>{b.name}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{b.os}</span>
          </div>
        ))}
      </div>

      {/* Aktif modeller */}
      {status?.is_enabled && (status?.models ?? []).length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>Downloaded Models</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(status?.models ?? []).map(model => (
              <div key={model.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'var(--card-bg)', border: `1px solid ${model.is_active ? '#3b82f6' : 'var(--border-color)'}`, borderRadius: '8px' }}>
                <Globe size={16} color={model.is_active ? '#3b82f6' : '#6b7280'} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{model.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{model.size_gb}GB · {model.vram_required_gb}GB VRAM</div>
                </div>
                {model.is_active && <span style={{ fontSize: '11px', padding: '2px 8px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', borderRadius: '4px' }}>Active</span>}
                {!model.is_active && (
                  <button onClick={() => { void handleActivate(model.id) }} style={{ padding: '5px 10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                    Activate
                  </button>
                )}
                <button onClick={() => { void handleDelete(model.id) }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!status?.is_enabled && (
        <div style={{ padding: '32px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px', marginBottom: '24px' }}>
          <Globe size={28} color="#3b82f6" style={{ margin: '0 auto 10px' }} />
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>WebLLM is disabled.</p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Requires Chrome 113+ or Edge 113+ with WebGPU and 4GB+ VRAM.</p>
        </div>
      )}

      {/* VRAM bazli model onerileri */}
      <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>Model Recommendations by VRAM</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {WEBGPU_VRAM_TIERS.map(tier => (
          <div key={tier.vram} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden' }}>
            <button
              onClick={() => setOpenVram(openVram === tier.vram ? null : tier.vram)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ width: '52px', height: '24px', borderRadius: '6px', background: `${tier.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: tier.color }}>{tier.badge}</span>
              </div>
              <span style={{ fontSize: '14px', fontWeight: 700, color: tier.color }}>{tier.vram} VRAM</span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{tier.note}</span>
              <div style={{ marginLeft: 'auto', color: 'var(--text-secondary)' }}>
                {openVram === tier.vram ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
            </button>
            {openVram === tier.vram && (
              <div style={{ borderTop: '1px solid var(--border-color)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {tier.models.map(model => (
                  <div key={model.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'var(--body-bg)', borderRadius: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{model.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{model.size_gb}GB download · {model.vram_required_gb}GB VRAM</div>
                    </div>
                    <span style={{ fontSize: '11px', padding: '2px 8px', background: `${tier.color}15`, border: `1px solid ${tier.color}40`, borderRadius: '4px', color: tier.color }}>{model.quality}</span>
                    {status?.is_enabled && (
                      <button
                        onClick={() => { void handleDownload(model.id) }}
                        disabled={downloading[model.id]}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: downloading[model.id] ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: downloading[model.id] ? 0.6 : 1 }}
                      >
                        <Download size={12} />
                        {downloading[model.id] ? 'Downloading...' : 'Download'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
